import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Upload, Trash2, Loader2, UsersRound } from "lucide-react";
import { toast } from "sonner";

const ResetButton = ({ onDone }) => {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/accounts/reset", { confirm: word });
      toast.success(`تم حذف ${data.deleted_accounts} حساب مع بياناتها`);
      setWord("");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <AlertDialog onOpenChange={() => setWord("")}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="rounded-full gap-2 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="accounts-reset-btn"><Trash2 className="w-4 h-4" /> تصفير الحسابات</Button>
      </AlertDialogTrigger>
      <AlertDialogContent dir="rtl" data-testid="accounts-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>تصفير كل الحسابات؟</AlertDialogTitle>
          <AlertDialogDescription className="text-start leading-relaxed">
            سيُحذف كل حساب عدا المدير: العملاء والورش والسطحات، مع الطلبات والمركبات والعروض والمحادثات والتقييمات. يبقى سجل المدفوعات كما هو. لا يمكن التراجع عن هذه الخطوة، لذا صدّر الحسابات أولاً إن احتجتها. للتأكيد اكتب كلمة «تصفير».
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="تصفير" data-testid="accounts-reset-confirm-input" />
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="accounts-reset-cancel">إلغاء</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={word.trim() !== "تصفير" || busy} className="bg-destructive hover:bg-destructive/90" data-testid="accounts-reset-confirm">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} حذف نهائي
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const AdminAccountTools = ({ onChanged }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState("");

  const doExport = async () => {
    setBusy("export");
    try {
      const res = await api.get("/admin/accounts/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `isnadcar-accounts-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("تعذّر تصدير الحسابات"); }
    setBusy("");
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("import");
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed.accounts)) throw new Error("bad");
      const { data } = await api.post("/admin/accounts/import", { accounts: parsed.accounts });
      toast.success(`تم استيراد ${data.imported} حساب • موجود مسبقاً ${data.skipped} • غير صالح ${data.invalid}`);
      onChanged();
    } catch (err) { toast.error(err.response ? formatApiError(err.response.data?.detail) : "الملف غير صالح"); }
    setBusy("");
  };

  return (
    <Card className="p-5 mb-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-testid="admin-account-tools">
      <div className="flex items-start gap-3">
        <UsersRound className="w-5 h-5 text-accent mt-0.5" />
        <div>
          <div className="font-bold">نقل وإدارة الحسابات</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">صدّر الحسابات من نسخة واستوردها في الأخرى. كلمات المرور تنتقل كما هي، ولا يُستبدل أي حساب موجود بنفس البريد.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full gap-2" onClick={doExport} disabled={!!busy} data-testid="accounts-export-btn">
          {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} تصدير الحسابات
        </Button>
        <Button variant="outline" className="rounded-full gap-2" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="accounts-import-btn">
          {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} استيراد الحسابات
        </Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={doImport} data-testid="accounts-import-input" />
        <ResetButton onDone={onChanged} />
      </div>
    </Card>
  );
};
